// Public entry point for the DDL edit applicator. Pure function — produces a
// `ModifiedDdlStatement` (with `plannedEdits[]`) for a single `DdlEdit`. v1
// covers column add/drop/rename + index add/drop. Five-op coverage matches
// the locked DSE3 op set; wider DDL is deferred to the v2 PRD.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: Column Edit Applicator
//      and § Slice: Index Edit Applicator.
// Cross-slice acceptance matrix: parity-required on edit-shape (DSE6),
// dialect-native on emitted SQL.
//
// emitSource is reused unchanged from SR-Edit (DSE1). The applicator's
// contract is "produce PlannedByteEdit[]"; bytes get patched downstream.
//
// DSE7: no v1 ALTER coalescing — every edit produces an independent
// PlannedByteEdit set. Two add-column calls in sequence emit two separate
// inserts (CREATE TABLE source path) or two separate ALTER statements
// (ALTER TABLE source path).
// v2 Slice H1 lifts DSE7 via the multi-edit `applyDdlEdits` entry-point
// below; the single-edit `applyDdlEdit` stays bit-for-bit identical to v1.

import { buildLineIndex, rangeFromOffsets, type LineIndex } from '../../../sql-grammar/document/lineIndex';
import type { ParseOptions, SqlEngine, StatementInfo } from '../../../sql-grammar/model';
import type {
  DdlEdit,
  DdlStructure,
  ModifiedDdlStatement,
  ParsedBuffer,
  PlannedByteEdit,
} from '../../types';

import { extractDdlStructure } from '../extractDdlStructure';
import { pickAppendInsertion } from './appendInsertion';
import { planAlterColumnEdit } from './alterColumnEdit';
import {
  coalesceAlterTableSynths,
  isCoalesceEligibleOp,
} from './coalesce/alterTableCoalescer';
import { planCheckConstraintEdit } from './checkConstraintEdit';
import { planColumnEdit } from './columnEdit';
import { planDatabaseEdit } from './databaseEdit';
import { planForeignKeyEdit } from './foreignKeyEdit';
import { planGrantEdit } from './grantEdit';
import { planIndexEdit } from './indexEdit';
import { planIndexLifecycleEdit } from './indexLifecycleEdit';
import { planNamedDefaultEdit } from './namedDefaultEdit';
import { planPackageEdit } from './packageEdit';
import { planPartitioningEdit } from './partitioningEdit';
import { planPrimaryKeyEdit } from './primaryKeyEdit';
import { planRoutineEdit } from './routineEdit';
import { planSchemaEdit } from './schemaEdit';
import { planSecurityEdit } from './securityEdit';
import { planSequenceEdit } from './sequenceEdit';
import { planTableLifecycleEdit } from './tableLifecycleEdit';
import { planTableOptionsEdit } from './tableOptionsEdit';
import { planTablespaceEdit } from './tablespaceEdit';
import { planTriggerEdit } from './triggerEdit';
import { planUniqueConstraintEdit } from './uniqueConstraintEdit';
import { planUserDefinedTypeEdit } from './userDefinedTypeEdit';
import { planViewEdit } from './viewEdit';
import {
  planOracleOnlineRedef,
  type OracleOnlineRedefOptions,
} from './recreate/oracleOnlineRedef';
import {
  isSqliteRecreateEligibleOp,
  planSqliteRecreate,
} from './recreate/sqliteRecreate';

export interface DdlEditCtx {
  readonly mysqlNoBackslashEscapes?: boolean;
  readonly source: string;
  readonly options: ParseOptions;
  readonly engine: SqlEngine;
  readonly lineIdx: LineIndex;
  readonly structure: DdlStructure;
  readonly statementInfo: StatementInfo;
}

/**
 * Ops whose planners append a fresh statement after the source and declare the
 * source statement kind "unrestricted" — they build the new statement purely
 * from the edit's own fields and never read `ctx.structure` (see the per-planner
 * headers in databaseEdit / schemaEdit / sequenceEdit / viewEdit / securityEdit).
 *
 * For these, a parked statement `extractDdlStructure` cannot classify (e.g. the
 * user parks a `CREATE DATABASE` and clicks [Edit Database]) must NOT abort the
 * edit. The substituted structure is inert: the append planners ignore it, and
 * `emitSource` consumes only `plannedEdits` — `originalStructure` is never read
 * downstream. We still rethrow for every other op so the loud-failure contract
 * (resolveDdlEdit lets extract/emit errors propagate) holds for structural edits
 * that genuinely depend on the parsed source shape.
 */
const STRUCTURE_INDEPENDENT_APPEND_OPS: ReadonlySet<DdlEdit['op']> = new Set([
  'create-database', 'alter-database', 'drop-database',
  'create-schema', 'alter-schema', 'drop-schema',
  'create-sequence', 'alter-sequence', 'drop-sequence',
  'create-view', 'drop-view',
  'create-materialized-view', 'drop-materialized-view', 'refresh-materialized-view',
  'create-login', 'alter-login', 'drop-login',
  'create-user', 'alter-user', 'drop-user',
  'create-role', 'alter-role', 'drop-role',
]);

function extractStructureForEdit(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
  op: DdlEdit['op'],
): DdlStructure {
  try {
    return extractDdlStructure(buffer, statementInfo);
  } catch (err) {
    if (STRUCTURE_INDEPENDENT_APPEND_OPS.has(op)) {
      // Inert placeholder — never read by the append planners or emitSource.
      return { kind: 'grant', range: statementInfo.range, parsed: null };
    }
    throw err;
  }
}

export function applyDdlEdit(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
  edit: DdlEdit,
  options: ApplyDdlEditOptions = {},
): ModifiedDdlStatement {
  const structure = extractStructureForEdit(buffer, statementInfo, edit.op);
  const source = buffer.parsedDocument.source;
  const parseOpts = buffer.parsedDocument.options;
  const lineIdx = buildLineIndex(source);
  const ctx: DdlEditCtx = {
    mysqlNoBackslashEscapes: options.mysqlNoBackslashEscapes,
    source, options: parseOpts, engine: parseOpts.engine, lineIdx, structure, statementInfo,
  };

  const planned = dispatchEditWithEngineOpts(ctx, edit, options);
  const sorted = [...planned].sort((a, b) => b.range.startOffset - a.range.startOffset);

  return {
    original: statementInfo,
    originalStructure: structure,
    applied: [edit],
    plannedEdits: sorted,
  };
}

/** Try the engine-specific opt-in apply paths first (sqlite recreate /
 *  oracle online-redefinition); fall through to the standard dispatcher when
 *  neither applies. */
function dispatchEditWithEngineOpts(
  ctx: DdlEditCtx,
  edit: DdlEdit,
  options: ApplyDdlEditOptions,
): readonly PlannedByteEdit[] {
  if (
    options.sqliteRecreate
    && ctx.engine === 'sqlite'
    && isSqliteRecreateEligibleOp(edit.op)
  ) {
    return planSqliteRecreate(
      ctx,
      edit as Parameters<typeof planSqliteRecreate>[1],
    );
  }
  const redef = planOracleOnlineRedef(ctx, edit, options.oracleOnlineRedef);
  if (redef !== undefined) return redef;
  return dispatchEdit(ctx, edit);
}

function dispatchEdit(ctx: DdlEditCtx, edit: DdlEdit): readonly PlannedByteEdit[] {
  switch (edit.op) {
    case 'add-column':
    case 'drop-column':
    case 'rename-column':
      return planColumnEdit(ctx, edit);
    case 'alter-column':
      return planAlterColumnEdit(ctx, edit);
    case 'add-index':
    case 'drop-index':
      return planIndexEdit(ctx, edit);
    case 'add-primary-key':
    case 'drop-primary-key':
      return planPrimaryKeyEdit(ctx, edit);
    case 'add-foreign-key':
    case 'drop-foreign-key':
      return planForeignKeyEdit(ctx, edit);
    case 'add-unique-constraint':
    case 'drop-unique-constraint':
      return planUniqueConstraintEdit(ctx, edit);
    case 'add-check-constraint':
    case 'drop-check-constraint':
      return planCheckConstraintEdit(ctx, edit);
    case 'add-named-default':
    case 'drop-named-default':
      return planNamedDefaultEdit(ctx, edit);
    case 'index-lifecycle':
      return planIndexLifecycleEdit(ctx, edit);
    case 'rename-table':
    case 'move-table-schema':
      return planTableLifecycleEdit(ctx, edit);
    case 'create-view':
    case 'drop-view':
    case 'create-materialized-view':
    case 'drop-materialized-view':
    case 'refresh-materialized-view':
      return planViewEdit(ctx, edit);
    case 'create-sequence':
    case 'alter-sequence':
    case 'drop-sequence':
      return planSequenceEdit(ctx, edit);
    case 'create-type':
    case 'drop-type':
      return planUserDefinedTypeEdit(ctx, edit);
    case 'create-schema':
    case 'alter-schema':
    case 'drop-schema':
      return planSchemaEdit(ctx, edit);
    case 'create-trigger':
    case 'alter-trigger':
    case 'drop-trigger':
      return planTriggerEdit(ctx, edit);
    case 'create-routine':
    case 'alter-routine':
    case 'drop-routine':
      return planRoutineEdit(ctx, edit);
    case 'create-package':
    case 'alter-package':
    case 'drop-package':
      return planPackageEdit(ctx, edit);
    case 'partition-table':
    case 'add-partition':
    case 'drop-partition':
    case 'create-partition-function':
    case 'create-partition-scheme':
    case 'switch-partition':
      return planPartitioningEdit(ctx, edit);
    case 'create-tablespace':
    case 'alter-tablespace':
    case 'drop-tablespace':
    case 'create-filegroup':
    case 'drop-filegroup':
    case 'add-database-file':
    case 'modify-database-file':
      return planTablespaceEdit(ctx, edit);
    case 'alter-table-options':
      return planTableOptionsEdit(ctx, edit);
    case 'create-login':
    case 'alter-login':
    case 'drop-login':
    case 'create-user':
    case 'alter-user':
    case 'drop-user':
    case 'create-role':
    case 'alter-role':
    case 'drop-role':
      return planSecurityEdit(ctx, edit);
    case 'grant':
    case 'revoke':
    case 'deny':
      return planGrantEdit(ctx, edit);
    case 'create-database':
    case 'alter-database':
    case 'drop-database':
      return planDatabaseEdit(ctx, edit);
  }
}

// ─── Multi-edit + coalesce (v2 Slice H1, lifts DSE7) ──────────────────────

/** Options for the `applyDdlEdit` / `applyDdlEdits` entry-points. */
export interface ApplyDdlEditOptions {
  /** SQL mode used to quote MySQL password DDL. Defaults to standard MySQL escaping. */
  readonly mysqlNoBackslashEscapes?: boolean;
  /** When `true`, merges N edits on the same alter-table target into a
   *  single multi-clause `ALTER TABLE` statement (oracle paren-grouped;
   *  sqlite falls back to multi-statement because sqlite ALTER TABLE is
   *  single-clause-only). When `false` (default — preserves DSE2-7
   *  behavior), N edits emit as N separate appended ALTER TABLE statements
   *  joined with `;\n` in a single PlannedByteEdit. */
  readonly coalesce?: boolean;
  /** Opt into the SQLite CREATE-COPY-RENAME-DROP recreate path for ALTER ops
   *  sqlite engines < 3.35.0 don't support directly (DROP COLUMN, RENAME
   *  COLUMN on old versions, ALTER COLUMN). Source structure must be
   *  `create-table` so the planner has the full column shape. When the
   *  engine is not sqlite or the op is not column-shaped, the option is a
   *  no-op; when the engine IS sqlite and the op IS column-shaped but the
   *  source is an ALTER TABLE statement (not CREATE TABLE), the planner
   *  throws instead of silently falling back — pass the table's CREATE
   *  TABLE statement as the source to use this path. Foreign-key disable
   *  wrapping is the consumer's responsibility. */
  readonly sqliteRecreate?: boolean;
  /** Opt into the Oracle online-redefinition path for
   *  `dbms_redefinition`-based column drops on large tables. Only applies on
   *  `oracle` engine + `drop-column` op + `create-table` source +
   *  `currentTableBytes >= sizeThresholdBytes`. Otherwise falls through to
   *  the direct `ALTER TABLE … DROP COLUMN` path. Size probe via
   *  `dba_segments.bytes` is the consumer's responsibility. */
  readonly oracleOnlineRedef?: OracleOnlineRedefOptions;
}

/** Apply N DdlEdits in a single pass against a single source statement.
 *  v2 Slice H1: lifts the v1 DSE7 punt — opt into `coalesce: true` to merge
 *  ALTER TABLE-shape edits into one multi-clause statement.
 *
 *  Eligibility for coalescing:
 *  - Source structure must be `alter-table` (CREATE TABLE source paths
 *    cannot coalesce — their per-edit semantics modify the column list in
 *    place, not by appending an ALTER statement).
 *  - All edits must target the same table (the source statement's table).
 *  - Op kinds limited to ALTER TABLE-shaped column / constraint adds and
 *    drops (see `isCoalesceEligibleOp`). `rename-column` on mssql uses
 *    `EXEC sp_rename` and is excluded; `alter-column` on mssql with a
 *    default-change emits multi-statement and is excluded.
 *
 *  The output is always a single `PlannedByteEdit` appending the merged
 *  statement (or multi-statement on sqlite / `coalesce: false`) after the
 *  source statement, mirroring the single-edit `applyDdlEdit` posture.
 *
 *  Behavior with one edit: equivalent to `applyDdlEdit` (the `coalesce`
 *  flag is a no-op). */
export function applyDdlEdits(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
  edits: readonly DdlEdit[],
  options: ApplyDdlEditOptions = {},
): ModifiedDdlStatement {
  if (edits.length === 0) {
    throw new Error(`applyDdlEdits: at least one edit required`);
  }
  if (edits.length === 1) {
    return applyDdlEdit(buffer, statementInfo, edits[0], options);
  }

  const structure = extractDdlStructure(buffer, statementInfo);
  const source = buffer.parsedDocument.source;
  const parseOpts = buffer.parsedDocument.options;
  const lineIdx = buildLineIndex(source);
  const ctx: DdlEditCtx = {
    mysqlNoBackslashEscapes: options.mysqlNoBackslashEscapes,
    source,
    options: parseOpts,
    engine: parseOpts.engine,
    lineIdx,
    structure,
    statementInfo,
  };

  if (options.coalesce) {
    rejectIneligibleForCoalesce(edits, structure);
  }

  const synths = edits.map((edit, i) => extractSingleAppendSynth(ctx, edit, i));

  const merged = options.coalesce
    ? coalesceAlterTableSynths(ctx, edits, synths)
    : synths.join(';\n');

  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(source, statementInfo.range.endOffset);

  return {
    original: statementInfo,
    originalStructure: structure,
    applied: edits,
    plannedEdits: [{
      range: rangeFromOffsets(lineIdx, insertAt, insertAt),
      replacement: sep + merged + trailSep,
      rationale: options.coalesce
        ? `coalesce-alter-table:${edits.length}`
        : `multi-edit:${edits.length}`,
    }],
  };
}

function rejectIneligibleForCoalesce(
  edits: readonly DdlEdit[],
  structure: DdlStructure,
): void {
  if (structure.kind !== 'alter-table') {
    throw new Error(
      `applyDdlEdits coalesce: source structure must be 'alter-table' (got '${structure.kind}'). `
        + `CREATE TABLE source paths cannot coalesce because their per-edit semantics `
        + `modify the column list in place rather than appending an ALTER statement.`,
    );
  }
  for (const edit of edits) {
    if (!isCoalesceEligibleOp(edit.op)) {
      throw new Error(
        `applyDdlEdits coalesce: op '${edit.op}' is not eligible for ALTER TABLE `
          + `coalescing. Drop 'coalesce: true' (multi-statement fallback runs the same `
          + `ops without merging) or split the edit out of the coalesced batch.`,
      );
    }
  }
}

function extractSingleAppendSynth(
  ctx: DdlEditCtx,
  edit: DdlEdit,
  index: number,
): string {
  const planned = dispatchEdit(ctx, edit);
  if (planned.length !== 1) {
    throw new Error(
      `applyDdlEdits: edit #${index} ('${edit.op}') produced ${planned.length} byte `
        + `edits — multi-edit + coalesce requires every per-edit planner to emit a `
        + `single appended statement. Pre-CREATE-TABLE-source column ops are not `
        + `supported in the multi-edit batch path; use single-edit applyDdlEdit instead.`,
    );
  }
  // Strip the leading separator (\n or ;\n) injected by pickAppendInsertion.
  const r = planned[0].replacement;
  if (r.startsWith(';\n')) return r.slice(2);
  if (r.startsWith('\n')) return r.slice(1);
  return r;
}
